/* Monthly leads bar chart — appears between the stat cards and the quick-filter chips.
 * Uses Recharts. Branded navy/accent. Compact (220–280px tall). */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE } from "@/lib/site";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ChevronDown, ChevronUp } from "lucide-react";
import DeferredChart from "@/pages/admin/DeferredChart";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-[var(--swipe-navy)] text-white px-3 py-2 rounded-lg shadow-lg text-[12px]">
      <div className="font-semibold">{p.label}</div>
      <div className="text-white/80">{p.count} lead{p.count === 1 ? "" : "s"}</div>
    </div>
  );
}

export default function MonthlyLeadsChart({ onPickMonth, activeMonth, source }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState([]);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    const params = { year };
    if (source && source !== "all") params.source = source;
    axios.get(`${API_BASE}/admin/leads/monthly`, { params, withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const rows = (r.data?.months || []).map((m) => ({
          month: m.month,
          label: `${MONTH_LABELS[parseInt(m.month.slice(-2), 10) - 1]} ${m.month.slice(0, 4)}`,
          shortLabel: MONTH_LABELS[parseInt(m.month.slice(-2), 10) - 1],
          count: m.count || 0,
        }));
        setData(rows);
      })
      .catch((err) => console.warn("[monthly-chart] load failed:", err))
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [year, source]);

  const total = useMemo(() => data.reduce((s, r) => s + r.count, 0), [data]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 mb-4" data-testid="monthly-chart">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <div>
          <div className="text-[15px] font-extrabold text-[var(--swipe-navy)] leading-tight">Lead Performance</div>
          <div className="text-[12px] text-slate-500 mt-0.5">
            Leads by Month · {year} · {total} total{source && source !== "all" ? ` · ${source}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))}
            data-testid="monthly-chart-year"
            className="text-[12px] px-2.5 py-1.5 rounded-lg border border-slate-200 outline-none focus:border-[var(--swipe-accent)] bg-white">
            {Array.from({ length: 5 }, (_, i) => currentYear - i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button type="button" onClick={() => setCollapsed((c) => !c)} title={collapsed ? "Expand" : "Collapse"}
            data-testid="monthly-chart-toggle"
            className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center justify-center text-slate-500 sm:hidden">
            {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {!collapsed ? (
        <DeferredChart height={300} testId="monthly-chart-wrapper" className="px-3 pt-2 pb-3">
          {busy && !data.length ? (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm">Loading…</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200} initialDimension={{ width: 600, height: 220 }}>
              <BarChart data={data} margin={{ top: 8, right: 10, left: -18, bottom: 4 }} barCategoryGap="22%">
                <XAxis dataKey="shortLabel" tick={{ fill: "#64748B", fontSize: 11, fontWeight: 600 }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(0, 114, 255, 0.05)" }} content={<ChartTooltip />} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} onClick={(d) => onPickMonth && onPickMonth(d?.month)}
                  style={{ cursor: onPickMonth ? "pointer" : "default" }}>
                  {data.map((d) => (
                    <Cell key={d.month} fill={activeMonth === d.month ? "#061A3A" : "#0072FF"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </DeferredChart>
      ) : null}
    </div>
  );
}
